import React, { useState } from 'react';
import styled from 'styled-components';
import { User, Team } from '../types';
import { UserAvatar } from './UserAvatar';
import EmojiConvertor from 'emoji-js';

interface TeamSectionProps {
  team: Team;
  users: User[];
  isFullWidth?: boolean;
  workspaceTeamId?: string;
  secondaryUsers?: User[];
  secondaryLabel?: string;
  tertiaryUsers?: User[];
  tertiaryLabel?: string;
}

const SectionContainer = styled.div<{ $isMeeting?: boolean; $isCustomStatus?: boolean; $isBookshelf?: boolean; $isOutOfOffice?: boolean }>`
  background: ${props => {
    if (props.$isMeeting) return 'rgba(255, 193, 7, 0.1)';
    if (props.$isOutOfOffice) return 'rgba(244, 67, 54, 0.1)';
    if (props.$isCustomStatus) return 'rgba(156, 39, 176, 0.1)';
    if (props.$isBookshelf) return 'rgba(139, 69, 19, 0.1)';
    return 'rgba(255, 255, 255, 0.05)';
  }};
  border-radius: 15px;
  padding: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid ${props => {
    if (props.$isMeeting) return 'rgba(255, 193, 7, 0.3)';
    if (props.$isOutOfOffice) return 'rgba(244, 67, 54, 0.3)';
    if (props.$isCustomStatus) return 'rgba(156, 39, 176, 0.3)';
    if (props.$isBookshelf) return 'rgba(139, 69, 19, 0.3)';
    return 'rgba(255, 255, 255, 0.1)';
  }};
  box-shadow: ${props => {
    if (props.$isMeeting) return '0 4px 16px rgba(255, 193, 7, 0.2)';
    if (props.$isOutOfOffice) return '0 4px 16px rgba(244, 67, 54, 0.2)';
    if (props.$isCustomStatus) return '0 4px 16px rgba(156, 39, 176, 0.2)';
    if (props.$isBookshelf) return '0 4px 16px rgba(139, 69, 19, 0.2)';
    return '0 4px 16px rgba(0, 0, 0, 0.3)';
  }};
  
  @media (min-width: 768px) {
    border-radius: 20px;
    padding: 30px;
    box-shadow: ${props => {
      if (props.$isMeeting) return '0 8px 32px rgba(255, 193, 7, 0.2)';
      if (props.$isOutOfOffice) return '0 8px 32px rgba(244, 67, 54, 0.2)';
      if (props.$isCustomStatus) return '0 8px 32px rgba(156, 39, 176, 0.2)';
      if (props.$isBookshelf) return '0 8px 32px rgba(139, 69, 19, 0.2)';
      return '0 8px 32px rgba(0, 0, 0, 0.3)';
    }};
  }
`;

const TeamHeader = styled.div`
  margin-bottom: 20px;
  text-align: center;
  
  @media (min-width: 768px) {
    margin-bottom: 25px;
  }
`;

const TeamName = styled.h2<{ $isClickable?: boolean }>`
  color: #ffffff;
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0 0 6px 0;
  cursor: ${props => props.$isClickable ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  
  &:hover {
    text-shadow: ${props => props.$isClickable ? '0 0 1px #ffffff, 0 0 2px #ffffff' : 'none'};
    font-weight: ${props => props.$isClickable ? '700' : '600'};
  }
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
    margin: 0 0 8px 0;
  }
`;

const TeamDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const DesksArea = styled.div<{ $compact?: boolean }>`
  position: relative;
  min-height: ${props => props.$compact ? 'auto' : '200px'};
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  padding: 15px;
  border: 2px dashed rgba(255, 255, 255, 0.1);
  box-sizing: border-box;
  overflow: visible;
  width: 100%;
  display: flex;
  flex-direction: column;

  @media (min-width: 768px) {
    min-height: ${props => props.$compact ? 'auto' : '300px'};
    border-radius: 15px;
    padding: 20px;
  }
`;

const DeskGrid = styled.div<{ $isFullWidth?: boolean; $itemCount?: number }>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(60px, 60px));
  gap: 15px;
  justify-items: center;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  overflow: visible;
  
  @media (min-width: 480px) {
    grid-template-columns: repeat(auto-fit, minmax(70px, 70px));
    gap: 18px;
  }
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(80px, 80px));
    gap: 20px;
  }
`;

const DeskSpot = styled.div<{ $occupied: boolean; $isOffline?: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: 10px;
  background: ${props => {
    if (!props.$occupied) return 'rgba(255, 255, 255, 0.1)';
    if (props.$isOffline) return 'linear-gradient(135deg, #666666 0%, #555555 100%)';
    return 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)';
  }};
  border: 2px solid ${props => {
    if (!props.$occupied) return 'rgba(255, 255, 255, 0.2)';
    if (props.$isOffline) return '#666666';
    return '#4CAF50';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.3s ease;
  box-shadow: ${props => {
    if (!props.$occupied) return '0 1px 4px rgba(0, 0, 0, 0.2)';
    if (props.$isOffline) return '0 2px 8px rgba(102, 102, 102, 0.3)';
    return '0 2px 8px rgba(76, 175, 80, 0.3)';
  }};

  @media (min-width: 480px) {
    width: 70px;
    height: 70px;
    border-radius: 11px;
    box-shadow: ${props => {
      if (!props.$occupied) return '0 2px 6px rgba(0, 0, 0, 0.2)';
      if (props.$isOffline) return '0 3px 12px rgba(102, 102, 102, 0.3)';
      return '0 3px 12px rgba(76, 175, 80, 0.3)';
    }};
  }

  @media (min-width: 768px) {
    width: 80px;
    height: 80px;
    border-radius: 12px;
    box-shadow: ${props => {
      if (!props.$occupied) return '0 2px 8px rgba(0, 0, 0, 0.2)';
      if (props.$isOffline) return '0 4px 15px rgba(102, 102, 102, 0.3)';
      return '0 4px 15px rgba(76, 175, 80, 0.3)';
    }};
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => {
      if (!props.$occupied) return '0 3px 10px rgba(0, 0, 0, 0.3)';
      if (props.$isOffline) return '0 4px 16px rgba(102, 102, 102, 0.4)';
      return '0 4px 16px rgba(76, 175, 80, 0.4)';
    }};
    
    @media (min-width: 768px) {
      transform: translateY(-2px);
      box-shadow: ${props => {
        if (!props.$occupied) return '0 4px 12px rgba(0, 0, 0, 0.3)';
        if (props.$isOffline) return '0 6px 20px rgba(102, 102, 102, 0.4)';
        return '0 6px 20px rgba(76, 175, 80, 0.4)';
      }};
    }
  }
`;

const EmptyDeskIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.3);
  position: relative;
  
  @media (min-width: 480px) {
    width: 28px;
    height: 28px;
    border-radius: 6px;
  }
  
  @media (min-width: 768px) {
    width: 30px;
    height: 30px;
    border-radius: 6px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 12px;
    height: 12px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.5);
    
    @media (min-width: 480px) {
      width: 14px;
      height: 14px;
      border-radius: 3px;
    }
    
    @media (min-width: 768px) {
      width: 16px;
      height: 16px;
      border-radius: 3px;
    }
  }
`;

const OnlineCount = styled.div`
  text-align: center;
  margin-top: auto;
  padding-top: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.8rem;

  @media (min-width: 768px) {
    padding-top: 15px;
    font-size: 0.9rem;
  }
`;

const StatusList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0;
  
  @media (min-width: 768px) {
    gap: 15px;
  }
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
  }
  
  @media (min-width: 768px) {
    padding: 15px;
    gap: 15px;
    border-radius: 10px;
  }
`;

const SmallAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  object-fit: cover;
  flex-shrink: 0;
  
  @media (min-width: 768px) {
    width: 40px;
    height: 40px;
    border: 2.5px solid #ffffff;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.25);
  }
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 2px;
  
  @media (min-width: 768px) {
    font-size: 1rem;
    margin-bottom: 4px;
  }
`;

const UserStatus = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 6px;
  
  @media (min-width: 768px) {
    font-size: 0.85rem;
  }
`;

// Custom Status full-width layout components
const CustomStatusGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;

  @media (min-width: 768px) {
    gap: 16px;
  }
`;

const CustomStatusCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: rgba(156, 39, 176, 0.15);
  border-radius: 20px;
  border: 1px solid rgba(156, 39, 176, 0.3);
  transition: all 0.2s ease;
  max-width: 300px;

  &:hover {
    background: rgba(156, 39, 176, 0.25);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(156, 39, 176, 0.2);
  }

  @media (min-width: 768px) {
    padding: 12px 16px;
    gap: 12px;
  }
`;

const CustomStatusAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid rgba(156, 39, 176, 0.5);
  flex-shrink: 0;
  object-fit: cover;

  @media (min-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

const CustomStatusInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const CustomStatusName = styled.span`
  color: white;
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const CustomStatusText = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 768px) {
    font-size: 0.8rem;
  }
`;

const StatusSubSection = styled.div`
  width: 100%;

  &:not(:first-child) {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const StatusSubLabel = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 16px;
  }
`;

// Bookshelf management UI components
const BookshelfControls = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 8px;
  align-items: center;
`;

const AddButton = styled.button`
  background: rgba(139, 69, 19, 0.8);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(139, 69, 19, 1);
    transform: translateY(-1px);
  }
  
  @media (min-width: 768px) {
    padding: 10px 20px;
    font-size: 0.9rem;
  }
`;

const ItemControls = styled.div`
  display: flex;
  gap: 6px;
  margin-left: auto;
`;

const ControlButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.7rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  &.edit {
    background: rgba(33, 150, 243, 0.3);
    &:hover {
      background: rgba(33, 150, 243, 0.5);
    }
  }
  
  &.delete {
    background: rgba(244, 67, 54, 0.3);
    &:hover {
      background: rgba(244, 67, 54, 0.5);
    }
  }
`;

const FormOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const FormModal = styled.div`
  background: #2d2d2d;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
`;

const FormTitle = styled.h3`
  color: white;
  margin: 0 0 20px 0;
  font-size: 1.2rem;
`;

const FormField = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  color: white;
  font-size: 0.9rem;
  margin-bottom: 6px;
  font-weight: 500;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.3);
  color: white;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: rgba(139, 69, 19, 0.8);
    box-shadow: 0 0 0 2px rgba(139, 69, 19, 0.2);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const FormButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const FormButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &.primary {
    background: rgba(139, 69, 19, 0.8);
    color: white;
    
    &:hover {
      background: rgba(139, 69, 19, 1);
    }
    
    &:disabled {
      background: rgba(139, 69, 19, 0.4);
      cursor: not-allowed;
    }
  }
  
  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

export const TeamSection: React.FC<TeamSectionProps> = ({ team, users, isFullWidth = false, workspaceTeamId, secondaryUsers = [], secondaryLabel, tertiaryUsers = [], tertiaryLabel }) => {

  const isMeetingSection = team.id === 'meeting';
  const isOutOfOfficeSection = team.id === 'out-of-office';
  const isCustomStatusSection = team.id === 'custom-status';
  const isBookshelfSection = team.id === 'bookshelf';

  // Bookshelf management state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formUrl, setFormUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // API base URL (same as used in main App)
  const API_BASE_URL = process.env.NODE_ENV === 'production' 
    ? window.location.origin
    : 'http://localhost:3001';

  // Bookshelf management functions
  const handleAddItem = () => {
    setFormTitle('');
    setFormUrl('');
    setEditingItem(null);
    setShowAddForm(true);
  };

  const handleEditItem = (user: User) => {
    setFormTitle(user.displayName);
    setFormUrl(user.statusText || '');
    setEditingItem(user.id);
    setShowAddForm(true);
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/bookshelf/${itemId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete item');
      }

      // The server will emit a real-time update via socket
    } catch (error) {
      console.error('Error deleting bookshelf item:', error);
      alert('Failed to delete item. Please try again.');
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formTitle.trim() || !formUrl.trim()) {
      alert('Please fill in both title and URL');
      return;
    }

    setIsSubmitting(true);

    try {
      const url = editingItem 
        ? `${API_BASE_URL}/api/bookshelf/${editingItem}`
        : `${API_BASE_URL}/api/bookshelf`;
      
      const method = editingItem ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: formTitle.trim(),
          url: formUrl.trim(),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save item');
      }

      // Reset form and close modal
      setShowAddForm(false);
      setFormTitle('');
      setFormUrl('');
      setEditingItem(null);

      // The server will emit a real-time update via socket
    } catch (error) {
      console.error('Error saving bookshelf item:', error);
      alert('Failed to save item. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormCancel = () => {
    setShowAddForm(false);
    setFormTitle('');
    setFormUrl('');
    setEditingItem(null);
  };

  // Convert Slack emoji format to actual emoji using emoji-js
  const getEmojiDisplay = (statusEmoji: string | undefined) => {
    if (!statusEmoji) return '';
    
    // If it's already a Unicode emoji (not wrapped in colons), return as-is
    if (!statusEmoji.startsWith(':') || !statusEmoji.endsWith(':')) {
      return statusEmoji;
    }
    
    // Create emoji converter instance
    const emoji = new EmojiConvertor();
    emoji.replace_mode = 'unified';
    emoji.allow_native = true;
    
    // Convert emoji shortcode to Unicode
    const converted = emoji.replace_colons(statusEmoji);
    
    // If conversion failed (same as input), it's likely a custom emoji - show chat bubble
    return converted !== statusEmoji ? converted : '💬';
  };

  // Mapping of team names to Jira board URLs
  const getJiraBoardUrl = (teamName: string) => {
    const urlMappings: { [key: string]: string } = {
      'iOS Devs': 'https://chatbookstown.atlassian.net/jira/software/c/projects/IOSP/boards/142',
      'Android Devs': 'https://chatbookstown.atlassian.net/jira/software/c/projects/AND/boards/143',
      'Web Devs': 'https://chatbookstown.atlassian.net/jira/software/c/projects/WEB/boards/144',
      'Core Devs': 'https://chatbookstown.atlassian.net/jira/software/c/projects/COR/boards/51?search=ombre',
      'Cross Platform Devs': 'https://chatbookstown.atlassian.net/jira/software/c/projects/FC/boards/145',
      'Data Engineering': 'https://chatbookstown.atlassian.net/jira/software/c/projects/DE/boards/251',
      // Add more team mappings as needed
    };
    
    return urlMappings[teamName] || null;
  };

  const handleTeamClick = () => {
    const jiraUrl = getJiraBoardUrl(team.name);
    if (jiraUrl) {
      window.open(jiraUrl, '_blank');
    }
  };
  
  // Show all users for all teams - offline users will be grayed out in the UserAvatar component
  const displayUsers = users;
  
  // Always show desks equal to total team size (not just online users)
  // This way DevOps with 2 people shows 2 desks, not 8
  const totalDesks = (isOutOfOfficeSection || isCustomStatusSection || isBookshelfSection || isMeetingSection)
    ? displayUsers.length // For status sections, show exact number with status
    : users.length; // For regular teams, show total team size
  
  return (
    <>
    <SectionContainer $isMeeting={isMeetingSection} $isOutOfOffice={isOutOfOfficeSection} $isCustomStatus={isCustomStatusSection} $isBookshelf={isBookshelfSection}>
      <TeamHeader>
        <TeamName
          $isClickable={!!getJiraBoardUrl(team.name)}
          onClick={handleTeamClick}
        >
          {team.name}
        </TeamName>
        {team.description && (
          <TeamDescription>{team.description}</TeamDescription>
        )}
      </TeamHeader>
      
      <DesksArea $compact={isCustomStatusSection && isFullWidth}>
        {/* Custom Status - Full Width Horizontal Layout */}
        {isCustomStatusSection && isFullWidth ? (
          <>
            {displayUsers.length > 0 && (
              <StatusSubSection>
                <StatusSubLabel>Custom Status</StatusSubLabel>
                <CustomStatusGrid>
                  {displayUsers.map((user) => (
                    <CustomStatusCard
                      key={user.id}
                      onClick={() => {
                        const slackUrl = workspaceTeamId
                          ? `slack://user?team=${workspaceTeamId}&id=${user.id}`
                          : `https://app.slack.com/client/T/D${user.id}`;
                        window.location.href = slackUrl;
                      }}
                    >
                      <CustomStatusAvatar
                        src={user.avatar}
                        alt={user.displayName}
                      />
                      <CustomStatusInfo>
                        <CustomStatusName>{user.displayName}</CustomStatusName>
                        <CustomStatusText>
                          {user.statusEmoji && <span>{getEmojiDisplay(user.statusEmoji)}</span>}
                          <span>{user.statusText || 'Custom status'}</span>
                        </CustomStatusText>
                      </CustomStatusInfo>
                    </CustomStatusCard>
                  ))}
                </CustomStatusGrid>
              </StatusSubSection>
            )}
            {secondaryUsers.length > 0 && (
              <StatusSubSection>
                <StatusSubLabel>{secondaryLabel || 'Out of Office'}</StatusSubLabel>
                <CustomStatusGrid>
                  {secondaryUsers.map((user) => (
                    <CustomStatusCard
                      key={user.id}
                      onClick={() => {
                        const slackUrl = workspaceTeamId
                          ? `slack://user?team=${workspaceTeamId}&id=${user.id}`
                          : `https://app.slack.com/client/T/D${user.id}`;
                        window.location.href = slackUrl;
                      }}
                      style={{ background: 'rgba(244, 67, 54, 0.15)', borderColor: 'rgba(244, 67, 54, 0.3)' }}
                    >
                      <CustomStatusAvatar
                        src={user.avatar}
                        alt={user.displayName}
                        style={{ borderColor: 'rgba(244, 67, 54, 0.5)' }}
                      />
                      <CustomStatusInfo>
                        <CustomStatusName>{user.displayName}</CustomStatusName>
                        <CustomStatusText>
                          {user.statusEmoji && <span>{getEmojiDisplay(user.statusEmoji)}</span>}
                          <span>{user.statusText || 'Out of office'}</span>
                        </CustomStatusText>
                      </CustomStatusInfo>
                    </CustomStatusCard>
                  ))}
                </CustomStatusGrid>
              </StatusSubSection>
            )}
            {tertiaryUsers.length > 0 && (
              <StatusSubSection>
                <StatusSubLabel>{tertiaryLabel || 'In a Meeting'}</StatusSubLabel>
                <CustomStatusGrid>
                  {tertiaryUsers.map((user) => (
                    <CustomStatusCard
                      key={user.id}
                      onClick={() => {
                        const slackUrl = workspaceTeamId
                          ? `slack://user?team=${workspaceTeamId}&id=${user.id}`
                          : `https://app.slack.com/client/T/D${user.id}`;
                        window.location.href = slackUrl;
                      }}
                      style={{ background: 'rgba(255, 193, 7, 0.15)', borderColor: 'rgba(255, 193, 7, 0.3)' }}
                    >
                      <CustomStatusAvatar
                        src={user.avatar}
                        alt={user.displayName}
                        style={{ borderColor: 'rgba(255, 193, 7, 0.5)' }}
                      />
                      <CustomStatusInfo>
                        <CustomStatusName>{user.displayName}</CustomStatusName>
                        <CustomStatusText>
                          {user.statusEmoji && <span>{getEmojiDisplay(user.statusEmoji)}</span>}
                          <span>{user.statusText || 'In a meeting'}</span>
                        </CustomStatusText>
                      </CustomStatusInfo>
                    </CustomStatusCard>
                  ))}
                </CustomStatusGrid>
              </StatusSubSection>
            )}
            <OnlineCount>
              {displayUsers.length + secondaryUsers.length + tertiaryUsers.length} {(displayUsers.length + secondaryUsers.length + tertiaryUsers.length) === 1 ? 'person' : 'people'}
            </OnlineCount>
          </>
        ) : (isCustomStatusSection || isBookshelfSection) ? (
          <>
            <StatusList>
              {displayUsers.map((user) => (
              <StatusItem
                key={user.id}
                onClick={(e) => {
                  // Don't trigger main click if clicking on control buttons
                  if ((e.target as HTMLElement).closest('.item-controls')) return;

                  if (isBookshelfSection && user.statusText) {
                    // For bookshelf, the "statusText" contains the URL; only open web links
                    if (/^https?:\/\//i.test(user.statusText)) {
                      window.open(user.statusText, '_blank', 'noopener,noreferrer');
                    }
                  } else {
                    // For custom status, open Slack profile
                    const slackUrl = workspaceTeamId
                      ? `slack://user?team=${workspaceTeamId}&id=${user.id}`
                      : `https://app.slack.com/client/T/D${user.id}`;
                    window.location.href = slackUrl;
                  }
                }}
              >
                {!isBookshelfSection && (
                  <SmallAvatar
                    src={user.avatar}
                    alt={user.displayName}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      const initials = user.displayName
                        .split(' ')
                        .map(name => name[0])
                        .join('')
                        .toUpperCase()
                        .substring(0, 2);
                      target.style.display = 'none';
                      target.parentElement!.innerHTML += `
                        <div style="
                          width: 32px;
                          height: 32px;
                          border-radius: 50%;
                          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          color: white;
                          font-weight: 600;
                          font-size: 0.8rem;
                          border: 2px solid #ffffff;
                          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
                          flex-shrink: 0;
                        ">${initials}</div>
                      `;
                    }}
                  />
                )}
                <UserInfo>
                  <UserName>{user.displayName}</UserName>
                  {!isBookshelfSection && (
                    <UserStatus>
                      {user.statusEmoji && <span>{getEmojiDisplay(user.statusEmoji)}</span>}
                      <span>{user.statusText || 'Custom status'}</span>
                    </UserStatus>
                  )}
                </UserInfo>
                {isBookshelfSection && (
                  <ItemControls className="item-controls">
                    <ControlButton
                      className="edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditItem(user);
                      }}
                    >
                      Edit
                    </ControlButton>
                    <ControlButton
                      className="delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteItem(user.id);
                      }}
                    >
                      Delete
                    </ControlButton>
                  </ItemControls>
                )}
              </StatusItem>
              ))}
            </StatusList>
            {isBookshelfSection && (
              <BookshelfControls>
                <AddButton onClick={handleAddItem}>
                  + Add Item
                </AddButton>
              </BookshelfControls>
            )}
          </>
        ) : (
          <>
            <DeskGrid $isFullWidth={isFullWidth} $itemCount={totalDesks}>
              {Array.from({ length: totalDesks }).map((_, index) => {
                const user = displayUsers[index];
                const isOccupied = !!user;
                
                return (
                  <DeskSpot key={index} $occupied={isOccupied} $isOffline={user && !user.isOnline}>
                    {isOccupied ? (
                      <UserAvatar user={user} workspaceTeamId={workspaceTeamId} />
                    ) : (
                      <EmptyDeskIcon />
                    )}
                  </DeskSpot>
                );
              })}
            </DeskGrid>
            
            <OnlineCount>
              {isOutOfOfficeSection || isCustomStatusSection || isBookshelfSection
                ? `${displayUsers.length} ${displayUsers.length === 1 ? 'item' : 'items'}`
                : `${users.filter(user => user.isOnline).length} of ${users.length} online`
              }
            </OnlineCount>
          </>
        )}
      </DesksArea>
    </SectionContainer>
    
    {/* Bookshelf Add/Edit Form Modal */}
    {showAddForm && (
      <FormOverlay onClick={(e) => e.target === e.currentTarget && handleFormCancel()}>
        <FormModal>
          <FormTitle>
            {editingItem ? 'Edit Bookshelf Item' : 'Add Bookshelf Item'}
          </FormTitle>
          <form onSubmit={handleFormSubmit}>
            <FormField>
              <FormLabel htmlFor="bookshelf-title">Title *</FormLabel>
              <FormInput
                id="bookshelf-title"
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="e.g., Team Handbook, API Docs, etc."
                required
                maxLength={100}
              />
            </FormField>
            
            <FormField>
              <FormLabel htmlFor="bookshelf-url">URL *</FormLabel>
              <FormInput
                id="bookshelf-url"
                type="url"
                value={formUrl}
                onChange={(e) => setFormUrl(e.target.value)}
                placeholder="https://example.com"
                required
              />
            </FormField>
            
            <FormButtons>
              <FormButton 
                type="button" 
                className="secondary" 
                onClick={handleFormCancel}
                disabled={isSubmitting}
              >
                Cancel
              </FormButton>
              <FormButton 
                type="submit" 
                className="primary"
                disabled={isSubmitting || !formTitle.trim() || !formUrl.trim()}
              >
                {isSubmitting ? 'Saving...' : (editingItem ? 'Update' : 'Add')}
              </FormButton>
            </FormButtons>
          </form>
        </FormModal>
      </FormOverlay>
    )}
    </>
  );
};
